import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { RoleType } from "@src/types/user";
import {
  createTrack,
  addTrackToPlaylist,
  uploadTrackFile,
  extractAudioMetadata,
  fetchApiTracks,
  CreateTrackFile,
  updateTrack,
  fetchTrackWithAudio,
  fetchTrackShazamInfo,
  fetchUntaggedTracks,
} from "@apis/trackApis";
import { QUERY_KEYS } from "./queryKeys";
import { ITrack } from "@src/types/track";
import { ApiResponse } from "@src/types/httpApi";
import { ILibraryFilters } from "@src/redux/slices/librarySlice";

// Create a new track mutation
type CreateTrackParams = {
  trackName: string;
  ownerId: string;
  fileId?: number;
  playlistId?: string;
  metadata?: Partial<ITrack>;
  audioData?: {
    url: string;
    name: string;
    mime: string;
    size: number;
    ext?: string;
  };
};
export const useCreateTrackMutation = (
  options?: UseMutationOptions<
    AxiosResponse<any>, // API response type
    Error, // Error type
    CreateTrackParams // Variables type
  >
): UseMutationResult<AxiosResponse<any>, Error, CreateTrackParams> => {
  return useMutation({
    mutationFn: ({
      trackName,
      ownerId,
      fileId,
      playlistId,
      metadata,
      audioData,
    }: CreateTrackParams) =>
      createTrack(trackName, ownerId, fileId, playlistId, metadata, audioData),
    ...options,
    mutationKey: [QUERY_KEYS.CREATE_TRACK],
  });
};

// Extract audio metadata mutation
export const useExtractAudioMetadataMutation = (
  options?: UseMutationOptions<
    AxiosResponse<any>,
    Error,
    { trackUrl: string; imagesUploadPath: string; jwt: string }
  >
): UseMutationResult<
  AxiosResponse<any>,
  Error,
  { trackUrl: string; imagesUploadPath: string; jwt: string }
> => {
  return useMutation({
    mutationFn: ({ trackUrl, imagesUploadPath, jwt }) =>
      extractAudioMetadata(trackUrl, imagesUploadPath, jwt),
    mutationKey: [QUERY_KEYS.EXTRACT_AUDIO_METADATA],
    ...options,
  });
};

// Add a track to a playlist mutation
export const useAddTrackToPlaylistMutation = (
  options?: UseMutationOptions<
    AxiosResponse<any>, // API response type
    Error, // Error type
    { trackId: string; playlistId: string } // Variables type
  >
): UseMutationResult<
  AxiosResponse<any>,
  Error,
  { trackId: string; playlistId: string }
> => {
  return useMutation({
    mutationFn: ({
      trackId,
      playlistId,
    }: {
      trackId: string;
      playlistId: string;
    }) => addTrackToPlaylist(trackId, playlistId),
    mutationKey: [QUERY_KEYS.ADD_TRACK_TO_PLAYLIST],
    ...options,
  });
};

// Upload a track file mutation
export const useUploadTrackFileMutation = (
  options?: UseMutationOptions<
    {
      fileUrl: string;
      audioData: {
        url: string;
        name: string;
        mime: string;
        size: number;
        ext?: string;
      };
    }, // API response type
    Error, // Error type
    CreateTrackFile // Variable type (trackFile and trackData)
  >
): UseMutationResult<
  {
    fileUrl: string;
    audioData: {
      url: string;
      name: string;
      mime: string;
      size: number;
      ext?: string;
    };
  },
  Error,
  CreateTrackFile
> => {
  return useMutation({
    mutationFn: (data: CreateTrackFile) => uploadTrackFile(data),
    ...options,
  });
};

export const useFetchTracksQuery = (
  filters: ILibraryFilters,
  curatorId?: number,
  role?: RoleType,
  options?: any
) => {
  return useInfiniteQuery({
    queryKey: [QUERY_KEYS.INFINITE_TRACKS, filters, curatorId],
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
      fetchApiTracks({ filters, page: pageParam, pageSize: 50 }),
    getNextPageParam: (
      lastPage: AxiosResponse<ApiResponse<ITrack[]>> | undefined
    ) => {
      if (!lastPage || !lastPage?.data?.meta?.pagination) {
        return undefined;
      }
      try {
        const { page: currentPage, pageCount: totalPages } =
          lastPage.data.meta.pagination;
        return currentPage < totalPages ? currentPage + 1 : undefined;
      } catch {
        return undefined;
      }
    },
    enabled: !!curatorId || role === "admin" || role === "manager",
    ...options,
  });
};
// Update a track mutation
export const useUpdateTrackMutation = (
  options?: UseMutationOptions<
    AxiosResponse<any>, // API response type
    Error, // Error type
    { trackId: string; trackData: Partial<ITrack> } // Variables type
  >
): UseMutationResult<
  AxiosResponse<any>,
  Error,
  { trackId: string; trackData: Partial<ITrack> }
> => {
  return useMutation({
    mutationFn: ({
      trackId,
      trackData,
    }: {
      trackId: string;
      trackData: Partial<ITrack>;
    }) => updateTrack(trackId, trackData),
    mutationKey: [QUERY_KEYS.UPDATE_TRACK],
    ...options,
  });
};

export const useFetchUntaggedTracksQuery = (
  filters?: {
    genre?: string[];
    emotion?: string[];
    tags?: string[];
  },
  options?: any
) => {
  return useInfiniteQuery({
    queryKey: [QUERY_KEYS.UNTAGGED_TRACKS, filters],
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
      fetchUntaggedTracks({
        page: pageParam,
        pageSize: 50,
        genreIds: filters?.genre,
        emotionIds: filters?.emotion,
        tagIds: filters?.tags,
      }),
    getNextPageParam: (
      lastPage: AxiosResponse<ApiResponse<ITrack[]>> | undefined
    ) => {
      if (!lastPage || !lastPage?.data?.meta?.pagination) {
        return undefined;
      }
      try {
        const { page: currentPage, pageCount: totalPages } =
          lastPage.data.meta.pagination;
        return currentPage < totalPages ? currentPage + 1 : undefined;
      } catch {
        return undefined;
      }
    },
    ...options,
  });
};

export const useTrackShazamInfoQuery = (
  trackId?: string,
  options?: Partial<UseQueryOptions<ITrack>>
): UseQueryResult<ITrack> => {
  return useQuery({
    queryKey: [QUERY_KEYS.TRACK_SHAZAM_INFO, trackId],
    queryFn: () => fetchTrackShazamInfo(trackId!),
    enabled: !!trackId,
    ...options,
  });
};

export const useFetchTrackAudioQuery = (trackId?: string, options?: any) => {
  return useQuery({
    queryKey: [QUERY_KEYS.TRACK_AUDIO, trackId],
    enabled: !!trackId,
    queryFn: () => fetchTrackWithAudio(trackId!),
    defaultValue: { data: { audio: "" } },
    ...options,
  });
};
